import { useCallback } from 'react';
import { useApiClient } from '~/api-client';
import { FederatedSearchQueryFormProps } from 'mark43-federated-search';
import { EventAutocompleteView, EventResource } from '@mark43/cad-api';
import {
    mapEventAutocompleteViewToOption,
    MINIMUM_SEARCH_STRING_LENGTH,
} from '../../forms/inputs/event-autocomplete';
import { globalAttributes } from '../../common/global-attributes';

type GetCadEventOptionsReturnType = ReturnType<
    NonNullable<FederatedSearchQueryFormProps['getCadEventOptions']>
>;

type GetCadEventOptionsFunction = (
    searchTerm?: string,
    eventNumber?: string
) => GetCadEventOptionsReturnType;

export const useGetCadEventOptions = () => {
    const apiClient = useApiClient();

    const getCadEventOptions: GetCadEventOptionsFunction = useCallback(
        async (searchTerm?: string, eventNumber?: string) => {
            const isInitialRender = searchTerm === undefined;
            const trimmedSearchTerm = searchTerm?.trim();

            if (Number(trimmedSearchTerm?.length) < MINIMUM_SEARCH_STRING_LENGTH) {
                return [];
            }

            let resultSearchTerm = trimmedSearchTerm || '';

            if (eventNumber && isInitialRender) {
                resultSearchTerm = eventNumber;
            }

            const eventAutocompleteResponse =
                await apiClient.request<EventResource.GetEventsByAutocomplete>({
                    method: 'POST',
                    data: {
                        query: resultSearchTerm?.trim(),
                        searchCallForService: false,
                        includeClearedEvents: true,
                        agencyTypeGlobalAttrIds: [globalAttributes.agencyTypeGlobal.POLICE],
                    },
                    url: 'event/autocomplete',
                });

            if (eventAutocompleteResponse.data) {
                const eventAutocompleteViews: EventAutocompleteView[] =
                    eventAutocompleteResponse.data.data?.eventAutocompleteViews ?? [];

                const options = eventAutocompleteViews.map(mapEventAutocompleteViewToOption);

                return options;
            }

            return [];
        },
        [apiClient]
    );

    return { getCadEventOptions };
};
